
<template>
  <div class="form-row" v-if="companyInfo">
    <form :class="{ 'loading-opacity': preloader, 'col-sm-12 p-4': isAdmin, 'col-md-12 mb-0': !isAdmin }">

      <!-- WHO WE ARE -->
      <div class="form-group">
        <h3 class="text-center mb-0">GDPR Privacy Notice</h3>
      </div>
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">Who We Are</label>
        <label class="col-sm-12 mb-sm-0">
          {{ companyInfo.company_name }} is committed to gathering and processing your personal information responsibly and in compliance with relevant data protection regulations and laws. This privacy notice outlines your rights, our obligations, and the reasons for processing your data.<br><br>
          <strong>Registered Office:</strong> {{ companyInfo.address }}<br>
          <strong>Company Registration Number:</strong> {{ companyInfo.comapny_number }}<br>
          <strong>ICO Registration Number:</strong> [Provide ICO number]<br>
          <strong>Data Controller:</strong> {{ companyInfo.company_name }}<br>
          <strong>Data Protection Officer:</strong> Contact Person, Tel: {{ companyInfo.company_phone }}
        </label>
      </div>

      <!-- PURPOSE -->
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">1. Purpose of this Notice</label>
        <label class="col-sm-12 mb-sm-0">
          This Privacy Notice explains how {{ companyInfo.company_name }} collects, uses, shares, and protects your personal data as part of our recruitment and vetting process in compliance with the UK GDPR, Data Protection Act 2018, and BS 7858:2019.
        </label>
      </div>

      <!-- LEGAL BASIS -->
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">2. Lawful Basis for Processing</label>
        <label class="col-sm-12 mb-sm-0">
          We collect and process your data under the following lawful bases (Article 6 UK GDPR):
          <ul>
            <li>Article 6(1)(b) – Employment contract performance</li>
            <li>Article 6(1)(c) – Legal compliance (e.g., BS7858)</li>
            <li>Article 6(1)(f) – Legitimate interests</li>
          </ul>
          For special categories and criminal data:
          <ul>
            <li>Article 9(2)(b) – Employment and social protection law</li>
            <li>Article 10 & Schedule 1, Part 1 – Criminal record processing</li>
          </ul>
        </label>
      </div>

      <!-- TYPES OF DATA -->
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">3. Types of Personal Data Collected</label>
        <label class="col-sm-12 mb-sm-0">
          We collect the following:
          <ul>
            <li>Identity and contact details</li>
            <li>Employment and education history</li>
            <li>Financial and criminal record data</li>
            <li>Health information (if relevant)</li>
            <li>Right-to-work, nationality, licensing (e.g., SIA)</li>
            <li>Equality and diversity (optional)</li>
          </ul>
        </label>
      </div>

      <!-- RETENTION -->
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">4. Retention of Your Data</label>
        <label class="col-sm-12 mb-sm-0">
          <ul>
            <li><strong>Successful applicant records:</strong> 7 years post-employment</li>
            <li><strong>Unsuccessful applications:</strong> 12 months</li>
            <li><strong>Criminal/financial checks:</strong> 6–7 years</li>
            <li><strong>Equality/diversity data:</strong> Deleted or anonymised post-use</li>
          </ul>
          Refer to our BS7858 & GDPR Screening Data Retention Schedule for full details.
        </label>
      </div>

      <!-- RIGHTS -->
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">5. Your Data Protection Rights</label>
        <label class="col-sm-12 mb-sm-0">
          You have the right to:
          <ul>
            <li>Access – Request a copy of your data</li>
            <li>Rectification – Request corrections</li>
            <li>Erasure – Request deletion (where lawful)</li>
            <li>Restriction – Pause processing</li>
            <li>Portability – Request in portable format</li>
            <li>Objection – Object to processing</li>
            <li>Withdraw consent – At any time</li>
          </ul>
          Contact our Data Protection Officer to exercise these rights.
        </label>
      </div>

      <!-- SHARING -->
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">6. Sharing Your Data</label>
        <label class="col-sm-12 mb-sm-0">
          Your data may be shared with authorised third parties for BS7858 screening:
          <ul>
            <li>Screening and vetting agencies</li>
            <li>Credit reference agencies</li>
            <li>Criminal record checking bodies</li>
            <li>Licensing authorities (e.g., SIA)</li>
            <li>Past employers, institutions, referees</li>
          </ul>
          All third parties operate under Data Processing Agreements (DPAs).
        </label>
      </div>

      <!-- SECURITY -->
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">7. Security Measures</label>
        <label class="col-sm-12 mb-sm-0">
          {{ companyInfo.company_name }} applies strong safeguards:
          <ul>
            <li>Role-based access and authentication</li>
            <li>Firewall and malware protection</li>
            <li>Secure digital and physical storage</li>
            <li>Staff training and audits</li>
          </ul>
        </label>
      </div>

      <!-- CONSENT FOR SENSITIVE DATA -->
      <div class="form-group row align-items-center">
        <label class="h4 col-sm-12">8. Explicit Consent for Special Categories of Data</label>
        <div class="col-sm-12">
          <label><app-input type="single-checkbox" :list-value-field="'I consent to the processing of my criminal record data for employment screening'" v-model="gdpr_info.criminal" /></label>
          <label><app-input type="single-checkbox" :list-value-field="'I consent to the processing of my financial history for role suitability'" v-model="gdpr_info.financial" /></label>
          <label><app-input type="single-checkbox" :list-value-field="'I consent to the processing of my health information, if required for safety or role fit'" v-model="gdpr_info.health" /></label>
          <label><app-input type="single-checkbox" :list-value-field="'I consent to the processing of equality and diversity data, where provided voluntarily'" v-model="gdpr_info.diversity" /></label>
          <label><app-input type="single-checkbox" :list-value-field="'I consent to the processing of my biometric data, if applicable to the role'" v-model="gdpr_info.biometric" /></label>
        </div>
      </div>

      <!-- SIGNATURE & DATE -->
      <div class="form-group row align-items-center">
        <label class="col-sm-12 mb-sm-0">
          Please sign and date to confirm your consent as outlined above.
        </label>

        <app-input class="col-sm-8 mr-sm-2 mb-sm-2"
                   type="single-checkbox"
                   :list-value-field="$t('I Agree')"
                   :required="true"
                   v-model="gdpr_info.consent" />

        <div class="col-sm-6">
          <label class="col-sm-12 mb-sm-0">Signature:</label>

          <!-- Show saved signature + "Update" button -->
          <div v-if="signatureSaved && gdpr_info.signature" style="border: dashed 0.5px;">
            <img :src="gdpr_info.signature" alt="Signature" width="400px" class="img-fluid">

            <div class="mt-2 no-print">
              <button
                  @click.prevent="startEditSignature"
                  class="btn btn-outline-primary btn-sm"
              >
                Update Signature
              </button>
            </div>
          </div>

          <!-- Signature pad (first time OR when editing) -->
          <div v-else class="no-print">
            <VueSignaturePad ref="signaturePad"
                             width="100%"
                             height="200px"
                             :options="options"
                             style="border: dashed 0.5px;"
                             :required="true" />
            <div class="mt-2">
              <button @click.prevent="clearSignature" class="btn btn-danger mr-2">
                Undo
              </button>
              <button @click.prevent="saveSignature" class="btn btn-success mr-2">
                Save
              </button>
              <!-- Optional: cancel editing and keep old signature -->
              <button
                  v-if="gdpr_info.signature"
                  @click.prevent="cancelEditSignature"
                  class="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        <label class="col-sm-12 mb-sm-0">Date</label>
        <app-input class="col-sm-6 mr-sm-2 mb-sm-2"
                   type="date"
                   :required="true"
                   v-model="gdpr_info.consent_date_time"
                   placeholder=""/>
      </div>

    </form>
  </div>
</template>


<script>
import {FormMixin} from "@/core/mixins/form/FormMixin";
export default {
  name: "GdprDetail",
  mixins: [FormMixin],
  props: {
    preloader: {
      type: Boolean,
      default: false
    },
    gdpr_info: {
      type: Object,
      default: () => ({})
    },
    companyInfo: {
      type: Object,
      default: () => ({})
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    onSaveBtn: {
      type: Function
    }
  },
  data() {
    return {
      isEditingSignature: false,
      signatureSaved: !!this.gdpr_info.signature,
      options: {
        penColor: "#000",

      },
    }
  },
  created(){
    this.$hub.$emit("candidateDetailActiveIndex", 8);

  },
  watch: {
    'gdpr_info.signature': {
      immediate: true,
      handler(val) {
        if (val) {
          this.signatureSaved = true;
        }
      }
    }
  },
  methods: {
    startEditSignature() {
      this.isEditingSignature = true;
      this.signatureSaved = false;
      this.$nextTick(() => {
        if (this.$refs.signaturePad) {
          const pad = this.$refs.signaturePad;
          pad.clearSignature ? pad.clearSignature() : (pad.clear ? pad.clear() : null);
        }
      });
    },
    cancelEditSignature() {
      this.isEditingSignature = false;
      this.signatureSaved = !!this.gdpr_info.signature;
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature
            ? this.$refs.signaturePad.clearSignature()
            : this.$refs.signaturePad.clear();
      }
    },
    clearSignature() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.undoSignature();
      }
    },
    saveSignature() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      if (!isEmpty) {
        this.gdpr_info.signature = data;
        this.isEditingSignature = false;
        this.signatureSaved = true;
        if (this.onSaveBtn) {
          this.onSaveBtn();
        }
      }
    }
  },
}
</script>

<style scoped>

</style>
